// Type-only, and type-only in the other direction too — `permissions` imports
// `TenantRole` from here. Both are erased at compile time, so the cycle exists
// only in the type graph and never at runtime.
import type { Permission, PermissionOverride } from '../permissions';

export interface ApiResponse {
  statusCode: number;
  headers: Record<string, string>;
  body: string;
}

export interface AppResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

/**
 * Mirrors the `plan_type` enum in supabase-schema.sql.
 *
 * `pro_trial` and `pro` are retired but not removed: Postgres cannot drop an
 * enum label while rows reference it, and subscribers on those plans are still
 * mid-period. TIER_LIMITS maps them onto the starter/growth limits so nobody's
 * plan silently changes shape under them.
 */
export type PlanType =
  'free_trial' | 'starter' | 'growth' | 'agency' | 'institution' | 'pro_trial' | 'pro';

export type SubscriptionStatus = 'active' | 'expired' | 'cancelled';

/**
 * What a plan is allowed to do. Every field is a hard limit enforced on the
 * backend — the frontend reads these to disable controls early, but a client
 * that ignores them still gets a 403.
 */
export interface TierLimits {
  brands: number;
  /** Connected social accounts, summed across every platform. */
  connectedAccounts: number;
  /** Counted when a post actually publishes, not when it is scheduled. */
  postsPerMonth: number;
  aiGenerationsPerMonth: number;
  mediaStorageMb: number;
  /** How far into the future a post may be scheduled. */
  scheduleHorizonDays: number;
  approvalWorkflow: boolean;
  analyticsRetentionDays: number;
  /** Platforms this plan may connect at all. */
  platforms: readonly SocialPlatform[];
}

export interface UserSubscription {
  plan: PlanType;
  status: SubscriptionStatus;
  periodStart: string;
  periodEnd: string;
  postsPublished: number;
  postsRemaining: number;
  aiGenerations: number;
  aiGenerationsRemaining: number;
  mediaBytes: number;
  limits: TierLimits;
  hasUsedFreeTrial: boolean;
}

// ─── Social domain ────────────────────────────────────────────────────────────
// Each of the four enums below mirrors a Postgres enum in supabase-schema.sql.
// Changing one requires changing the other, in the order the migration notes
// spell out: the label must be committed to Postgres before any code inserts it.

export const SOCIAL_PLATFORMS = ['facebook', 'instagram'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const INDUSTRY_VERTICALS = [
  'restaurant',
  'dental',
  'real_estate',
  'cleaning',
  'agency',
  'other',
] as const;
export type IndustryVertical = (typeof INDUSTRY_VERTICALS)[number];

/**
 * The post lifecycle.
 *
 * `publishing` is a lease, not a user-visible state: the scheduler flips a
 * variant into it to claim the row, and either the publisher resolves it or the
 * stale-claim sweeper returns it to `scheduled`.
 */
export const POST_STATUSES = [
  'draft',
  'pending_review',
  'approved',
  'scheduled',
  'publishing',
  'published',
  'failed',
  'cancelled',
] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/**
 * `needs_reauth` is a first-class state rather than an error. Meta revokes page
 * tokens on password and permission changes, TikTok rotates its refresh token on
 * every use, and YouTube expires refresh tokens weekly while its consent screen
 * sits in Testing. A dead token stops a customer's whole calendar, so it has to
 * be something the UI can show rather than something discovered at 6am.
 */
export const SOCIAL_ACCOUNT_STATUSES = ['active', 'needs_reauth', 'revoked', 'disabled'] as const;
export type SocialAccountStatus = (typeof SOCIAL_ACCOUNT_STATUSES)[number];

export type BrandTone = 'playful' | 'professional' | 'warm' | 'authoritative' | 'luxury';

/**
 * The business context every generation reads.
 *
 * Persisted as JSONB on `brands.profile` rather than as columns, because it is
 * always read whole, never queried field-by-field, and its shape changes with
 * prompt engineering rather than with migrations. It is also the cached prompt
 * prefix on every model call — stable per brand, and large enough that caching
 * it pays for itself immediately.
 */
export interface BrandProfile {
  tagline?: string;
  description?: string;
  services: string[];
  tone: BrandTone;
  audience?: string;
  locality?: string;
  /**
   * Language the *content* is generated in. Deliberately separate from the UI
   * language: a Bengali-speaking owner may well post in English, and switching
   * the interface must never silently change what goes out to their audience.
   */
  languages: string[];
  offers: string[];
  brandVoiceNotes?: string;
  bannedPhrases: string[];
  callsToAction: string[];
  links: { website?: string; booking?: string; menu?: string };
  hashtagSets: { core: string[]; rotating: string[]; local: string[] };
  colorHex?: string;
  logoAssetId?: string;
}

export interface Brand {
  id: string;
  name: string;
  vertical: IndustryVertical;
  profile: BrandProfile;
  timezone: string;
  approvalRequired: boolean;
  createdAt: string;
}

/** The fields required to create a new brand. */
export interface CreateBrandInput {
  name: string;
  vertical: IndustryVertical;
  /** IANA timezone string, e.g. "Asia/Dhaka". */
  timezone: string;
}

export type MediaAssetStatus = 'pending' | 'ready' | 'archived';
export type MediaAssetType = 'image' | 'video';

/**
 * A media asset as the frontend is allowed to see it.
 *
 * The S3 key, encryption details and raw CDN path never leave the backend —
 * `url` is a signed or public CDN URL the client can render directly.
 *
 * `ownerId` / `ownerEmail` are present only in the admin projection so a
 * superadmin can see who uploaded a file without joining to auth.users on the
 * client. Creators receive these as null.
 */
export interface MediaAsset {
  id: string;
  ownerId: string;
  ownerEmail: string | null;
  name: string;
  mimeType: string;
  type: MediaAssetType;
  sizeBytes: number;
  url: string;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  altText: string | null;
  status: MediaAssetStatus;
  /** User IDs this asset has been explicitly shared with by an admin. */
  sharedWith: string[];
  createdAt: string;
}

export interface MediaUploadUrlResponse {
  mediaId: string;
  uploadUrl: string;
  /** Extra form fields required by the presigned POST (AWS S3 multipart). */
  fields: Record<string, string>;
}

/**
 * A connected publishing destination, as the frontend is allowed to see it.
 *
 * Deliberately a redacted projection of `social_accounts`: the token ciphertext,
 * wrapped data key and key id exist on the row but must never cross the wire,
 * and there is no client feature that needs them.
 */
export interface SocialAccountSummary {
  id: string;
  brandId: string;
  platform: SocialPlatform;
  externalId: string;
  externalName: string | null;
  avatarUrl: string | null;
  status: SocialAccountStatus;
  statusReason: string | null;
  accessExpiresAt: string | null;
}

// ─── OAuth connect flow ───────────────────────────────────────────────────────
// Three phases, and the middle one is why there are three:
//
//   initialize → the browser leaves for the provider
//   callback   → the provider comes back, the code is exchanged, and the
//                connectable targets are discovered
//   finalize   → the user picks which of those targets to connect
//
// The callback cannot finish the job alone, because at that point nobody has
// said what to connect: one Meta login routinely returns several Pages, and
// connecting all of them is not what "connect Facebook" means to anyone. Every
// platform added later has the same shape — an account-level login followed by a
// choice of destination — so the phases are the generic part, and only the
// provider adapter differs.

/** Where the browser is sent, and the state that must come back with it. */
export interface OAuthInitializeResponse {
  authorizeUrl: string;
  state: string;
}

/**
 * One thing the user could connect, as discovered during the callback.
 *
 * `metadata` is provider-specific and opaque to the client — a Page id for
 * Instagram, an organization URN for LinkedIn. It carries no credentials: the
 * Page access tokens Meta hands back alongside each Page are stripped before
 * this ever leaves the backend.
 */
export interface ConnectTarget {
  id: string;
  name: string;
  avatarUrl: string | null;
  /** Human hint under the name — "Facebook Page", "@handle". */
  subtitle: string | null;
  /** True when this target is already connected to the brand. */
  alreadyConnected: boolean;
  metadata: Record<string, string>;
}

/** The pending connection the picker renders, read between callback and finalize. */
export interface OAuthPendingConnection {
  id: string;
  platform: SocialPlatform;
  brandId: string;
  targets: ConnectTarget[];
  expiresAt: string;
}

export interface OAuthFinalizeRequest {
  /** `ConnectTarget.id` values. Everything not listed is discarded with the token. */
  targetIds: string[];
}

/** Query parameter the callback redirects back to the SPA with. */
export const OAUTH_CONNECTION_PARAM = 'connection';
export const OAUTH_ERROR_PARAM = 'oauth_error';

/** How long a pending connection stays claimable. Minutes, not hours — it holds a live token. */
export const OAUTH_PENDING_TTL_MINUTES = 15;
export const OAUTH_STATE_TTL_MINUTES = 10;

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled';

/** Billing currency. Drives which gateway a checkout is routed to. */
export type Currency = 'BDT' | 'USD';

export interface PaymentRecord {
  id: string;
  tranId: string;
  plan: PlanType;
  amount: number;
  status: PaymentStatus;
  createdAt: string;
}

export interface InitiatePaymentResponse {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

export interface InitiatePaymentResult {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

// ─── Roles ────────────────────────────────────────────────────────────────────

/**
 * Held in the Supabase user's `app_metadata`, which only the service-role key and
 * the dashboard can write — a user cannot promote themselves. Never read this
 * from `user_metadata`, which is self-writable and therefore worthless for
 * authorization. A missing role means `'user'`.
 */
export type UserRole = 'user' | 'superadmin';

export const SUPERADMIN_ROLE: UserRole = 'superadmin';

// ─── Audit log ────────────────────────────────────────────────────────────────

/**
 * Mirrors the `activity_type` enum in supabase-schema.sql. Change both together.
 *
 * ADD labels here, never remove them. `activity_log` is append-only and holds
 * years of rows; `question_generated` is retired in favour of
 * `content_generated`, but dropping it would break the admin feed's rendering of
 * every historical row and reset lifetime counters to zero.
 */
export const ACTIVITY_TYPES = [
  'user_signed_up',
  'user_signed_in',
  'content_generated',
  'generation_blocked',
  'generation_failed',
  'brand_created',
  'account_connected',
  'account_disconnected',
  'account_reauth_required',
  'post_scheduled',
  'post_published',
  'post_failed',
  'post_approved',
  'post_rejected',
  'token_refresh_failed',
  'payment_initiated',
  'payment_completed',
  'payment_failed',
  'subscription_activated',
  'subscription_cancelled',
  'user_banned',
  'user_unbanned',
  'tenant_created',
  'tenant_deleted',
  'tenant_plan_changed',
  'tenant_admin_changed',
  'member_invited',
  'member_joined',
  'member_removed',
  'member_role_changed',
  'permission_granted',
  'permission_revoked',
  'team_created',
  'team_deleted',
  'team_membership_changed',
  'team_visibility_changed',
  /** Retired: the question-generator era. Kept so old rows still render. */
  'question_generated',
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/**
 * Free-form per-event detail. Deliberately metadata only — no caption, post body
 * or media ever goes in here, and failures carry an upstream status code without
 * the upstream message, which can quote our API key or an access token back at
 * us.
 */
export type ActivityMetadata = Record<string, string | number | boolean | string[] | null>;

export interface ActivityLogEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  type: ActivityType;
  metadata: ActivityMetadata;
  createdAt: string;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminUserSummary {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  role: UserRole;
  plan: PlanType | null;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  createdAt: string;
  lastSignInAt: string | null;
  totalGenerations: number;
  /**
   * `auth.users.banned_until`, the authoritative ban flag. Null when never
   * banned; a past timestamp when a ban has since expired. Distinct from
   * `status` above, which is the *subscription* lifecycle.
   */
  bannedUntil: string | null;
}

// ─── Bans ─────────────────────────────────────────────────────────────────────

/**
 * Ban durations are stored in GoTrue as a Go duration string. An indefinite ban
 * is expressed as ~100 years rather than a null, because `banned_until` is the
 * only field the auth layer checks — there is no separate "forever" flag.
 */
export const INDEFINITE_BAN_HOURS = 876_000;

/** Anything expiring beyond this is a `INDEFINITE_BAN_HOURS` ban, not a real date. */
export const INDEFINITE_BAN_THRESHOLD_MS = 50 * 365 * 24 * 60 * 60 * 1000;

export function isIndefiniteBan(bannedUntil: string): boolean {
  return new Date(bannedUntil).getTime() - Date.now() > INDEFINITE_BAN_THRESHOLD_MS;
}

/** True only for a ban that is still in force — an expired `banned_until` is not a ban. */
export function isBanActive(bannedUntil: string | null | undefined): boolean {
  return Boolean(bannedUntil && new Date(bannedUntil).getTime() > Date.now());
}

/**
 * Upper bound on a fixed-length ban. Anything longer is an indefinite ban, which
 * is expressed by omitting `days` rather than by asking for 10,000 of them.
 */
export const BAN_MAX_DAYS = 365;
export const BAN_REASON_MAX_LENGTH = 500;

// ─── Renewal reminders ────────────────────────────────────────────────────────

/**
 * How long an expired subscription stays quiet after a reminder before prompting
 * again. There is no cap on the number of reminders: an account that stays
 * expired keeps being asked, because the prompt stops the moment it works.
 */
export const RENEWAL_REMINDER_INTERVAL_DAYS = 15;

export const RENEWAL_REMINDER_INTERVAL_MS = RENEWAL_REMINDER_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Whether an expired subscription is due to prompt the user to subscribe again.
 *
 * Deliberately measured from the last reminder rather than from `period_end`, so a
 * dismissal always buys a full quiet interval. Anchoring to expiry instead would
 * let someone who dismissed on day 14 be prompted again on day 15.
 *
 * Lives in `common/` because it is the whole rule: whatever delivers the reminder
 * — today the dashboard banner, later a scheduled sweep — must agree on when one
 * is owed, and a second copy of this arithmetic is how the two would drift apart.
 */
export function isRenewalReminderDue(
  status: SubscriptionStatus,
  lastRemindedAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (status !== 'expired') return false;
  if (!lastRemindedAt) return true;

  const remindedAt = new Date(lastRemindedAt).getTime();
  // An unparseable timestamp is treated as "never reminded". The alternative is a
  // NaN comparison, which is always false — silently muting the prompt forever.
  if (Number.isNaN(remindedAt)) return true;

  return now - remindedAt >= RENEWAL_REMINDER_INTERVAL_MS;
}

export interface BanUserRequest {
  reason: string;
  /** Omitted means indefinite. */
  days?: number;
}

export interface ActiveBan {
  reason: string | null;
  bannedUntil: string | null;
}

/** The 403 body a banned caller gets from every authenticated endpoint. */
export const ACCOUNT_BANNED_ERROR = 'ACCOUNT_BANNED';

/** Shape of the `admin_stats(days)` RPC's JSONB. Change both together. */
export interface AdminStats {
  totalUsers: number;
  newUsers: number;
  activeSubscriptions: number;
  totalGenerations: number;
  generationsInPeriod: number;
  totalPostsPublished: number;
  postsPublishedInPeriod: number;
  connectedAccounts: number;
  /**
   * Accounts whose token died and that the customer has not reconnected. The one
   * number here worth alerting on: it is silent churn, because a brand in this
   * state has a calendar that quietly stops publishing.
   */
  accountsNeedingReauth: number;
  revenueInPeriod: number;
  planDistribution: Partial<Record<PlanType, number>>;
  platformDistribution: Partial<Record<SocialPlatform, number>>;
  activityByType: Partial<Record<ActivityType, number>>;
  generationsPerDay: { date: string; count: number }[];
  postsPublishedPerDay: { date: string; count: number }[];
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Tenancy ──────────────────────────────────────────────────────────────────
// A tenant is the unit of ownership and billing. A user holds a role *inside* a
// tenant and nowhere else; `UserRole` above is the platform-wide role and has
// exactly two values, only one of which means anything (`superadmin`).
//
// A superadmin is deliberately not a tenant member. They administer tenants
// without occupying a seat in one, so they never appear in the roster, never
// count toward MAX_TENANT_ADMINS, and are never in the team-visibility graph.

/** Mirrors the `tenant_role` enum in supabase-migration-tenancy.sql. */
export const TENANT_ROLES = ['admin', 'member'] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

/** Mirrors the `invitation_status` enum. `pending` past `expiresAt` is dead, not a state. */
export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export interface Tenant {
  id: string;
  name: string;
  /** URL-safe, unique across the platform. Derived from `name` at creation. */
  slug: string;
  createdAt: string;
}

/**
 * The authorization context resolved for every tenant-scoped request, before the
 * handler runs.
 *
 * Returned to the frontend as-is so the UI gates on the same object the API
 * refuses on — a hidden control is then exactly a control that would have 403'd,
 * rather than a second guess at the rules.
 */
export interface TenantContext {
  tenantId: string;
  /** Null for a superadmin, who holds every permission without being a member. */
  role: TenantRole | null;
  permissions: Permission[];
  /** Teams the caller belongs to inside this tenant. */
  teamIds: string[];
  /**
   * Teams whose work the caller may see beyond their own — the narrow grants an
   * admin issued. Empty and irrelevant when the caller holds `team.view.all`.
   */
  visibleTeamIds: string[];
  isPlatformOperator: boolean;
}

/** One tenant as the signed-in user sees it, with their own standing inside it. */
export interface TenantMembershipSummary {
  tenant: Tenant;
  role: TenantRole;
  permissions: Permission[];
  teamIds: string[];
  memberCount: number;
  joinedAt: string;
}

/** A member of a tenant, as the roster shows them to an admin. */
export interface TenantMemberSummary {
  userId: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  role: TenantRole;
  /** Role defaults with the overrides already applied. */
  permissions: Permission[];
  /** Only the deltas an admin set, so the UI can show what was deliberately changed. */
  overrides: PermissionOverride[];
  teamIds: string[];
  joinedAt: string;
}

export interface Team {
  id: string;
  tenantId: string;
  name: string;
  memberIds: string[];
  createdAt: string;
}

export interface TenantInvitationSummary {
  id: string;
  email: string;
  role: TenantRole;
  status: InvitationStatus;
  invitedByEmail: string | null;
  expiresAt: string;
  createdAt: string;
}

/** How long an invitation stays claimable. */
export const INVITATION_TTL_DAYS = 14;

export const TENANT_NAME_MAX_LENGTH = 100;
export const TEAM_NAME_MAX_LENGTH = 60;

/**
 * A tenant's plan.
 *
 * Deliberately smaller than `UserSubscription`: usage counters live in
 * `usage_tracking`, which is keyed by user, so a tenant's consumption is a sum
 * over its roster and is served by `TenantStats` rather than pretended to be a
 * property of the subscription row.
 */
export interface TenantSubscription {
  tenantId: string;
  plan: PlanType;
  status: SubscriptionStatus;
  periodStart: string;
  periodEnd: string;
  limits: TierLimits;
}

/** One row of the superadmin's tenant list. */
export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  memberCount: number;
  adminCount: number;
  /** Null until a plan is attached — a tenant can exist without a subscription row. */
  plan: PlanType | null;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  admins: { userId: string; email: string | null }[];
  createdAt: string;
}

/** Shape of the `tenant_stats(uuid, int)` RPC's JSONB. Change both together. */
export interface TenantStats {
  tenantId: string;
  memberCount: number;
  adminCount: number;
  pendingInvitations: number;
  teamCount: number;
  brandCount: number;
  plan: PlanType | null;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  postsPublished: number;
  aiGenerations: number;
  mediaBytes: number;
  activityInPeriod: number;
  activityByType: Partial<Record<ActivityType, number>>;
}
